import { Link } from 'react-router-dom';
import styled, { DefaultTheme, css } from 'styled-components/macro';

import Button from 'components/Button/Button';
import ButtonIcon from 'components/ButtonIcon/ButtonIcon';
import FlexContainer from 'components/FlexContainer/FlexContainer';
import Typography from 'components/Typography/Typography';
import { TicketsReportCardI } from 'declarations/interfaces/reports';

type GradeStylesT = {
  grade: TicketsReportCardI['grade'];
};

type ReportStateStyledT = {
  isReportOpened: boolean;
};

type CardWrapStyledT = {
  isActive: boolean;
};

type StatisticSignStyledT = {
  isPositive: boolean;
};

type InvoicePercentageStyledT = {
  isPositive: boolean;
};

const reportHeaderHeight = 48;
const cardsBlockHeight = 185;
const cardsClosedTranslateValue = -(reportHeaderHeight + cardsBlockHeight);

const A = css`
  color: ${({ theme }) => theme.colors.primary.variant11};
`;
const B = css`
  color: ${({ theme }) => theme.colors.primary.variant8};
`;
const C = css`
  color: ${({ theme }) => theme.colors.primary.variant7};
`;
const D = css`
  color: ${({ theme }) => theme.colors.highlight.variant2};
`;
const F = css`
  color: ${({ theme }) => theme.colors.highlight.variant2};
`;

const gradeColor = {
  A,
  B,
  C,
  D,
  F,
};

export const DealerReportWrapStyled = styled.div`
  position: relative;
  width: 100%;
`;

export const MessageBoxStyled = styled(FlexContainer)`
  width: 100%;
  max-width: 800px;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.white};
  left: 0;
  padding: 2px;
  border: ${({ theme }) => theme.borders.md};
  border-radius: ${({ theme }) => theme.radius.lg};
  color: ${({ theme }) => theme.colors.highlight.variant2};
  ${({ theme }) => `
    ${theme.devices.tablet} {
      width: 90%;
    }
  `}
`;

export const MessageBoxContentStyled = styled(Typography)`
  width: 100%;
  height: 100%;
  max-height: 70px;
  padding: 4px;
  overflow-y: auto;
`;

export const CardsWrapStyled = styled(FlexContainer)<ReportStateStyledT>`
  width: 100%;
  position: relative;
  max-height: ${({ isReportOpened }) => (isReportOpened ? '200px' : '0')};
  overflow-x: auto;
  overflow-y: hidden;
  transform: ${({ isReportOpened }) =>
    isReportOpened ? 'translateY(0)' : `translateY(${cardsClosedTranslateValue}px)`};
  z-index: 0;
  transition: max-height 0.25s linear, transform 0.3s linear;
`;

export const CardWrapStyled = styled(FlexContainer)<CardWrapStyledT>`
  flex-shrink: 0;
  min-width: 300px;
  margin: 10px;
  padding: 10px;
  position: relative;
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ isActive, theme }) =>
    isActive ? theme.colors.primary.variant5 : theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.primary.variant6};
  ${({ theme }) => `
    ${theme.devices.desktop} {
      margin: 10px 10px 10px 0;
    }
  `}
`;

export const InnerContentStyled = styled(FlexContainer)`
  align-self: baseline;
  gap: 10px;
  width: 100%;
  margin-bottom: 10px;
  position: relative;
`;

export const InnerContentRowStyled = styled(FlexContainer)`
  width: 100%;
`;

export const ClaimReportInnerContentStyled = styled(InnerContentStyled)`
  gap: 3px;
  > div:not(:first-child) {
    margin-bottom: 9px;
  }
`;

export const CategoryLabelStyled = styled(Typography)`
  margin-top: 2px;
  font-size: 0.5rem;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.primary.variant9};
  position: relative;
`;

export const MessageStyled = styled(Typography)`
  margin-top: 4px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  position: relative;
`;

export const GradesWrapStyled = styled.div`
  flex-shrink: 0;
  position: relative;
`;

export const GradeLabelStyled = styled(Typography)`
  font-size: 0.5rem;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.black};
  line-height: 1.3;
  opacity: 0.3;
  position: relative;
`;

export const AvgClaimAmountLabelStyled = styled.div`
  margin-right: 10px;
`;

export const PointStyled = styled(Typography)`
  font-weight: ${({ theme }) => theme.fontWeight.extraBold};
  font-size: ${({ theme }) => theme.fontSize['2xl']};
  color: ${({ theme }) => theme.colors.primary.variant11};
  position: relative;
`;

export const GradeItemStyled = styled.div`
  margin-top: 16px;
  &:first-child {
    margin-top: 0;
  }
  position: relative;
`;

export const FooterWrapStyled = styled(FlexContainer)`
  width: 100%;
  height: 100%;
  min-height: 38px;
  background: ${({ theme }) => theme.colors.primary.variant3};
  padding: 7px 9px;
  border-radius: ${({ theme }) => theme.radius.lg};
  position: relative;
`;

export const FooterStyled = styled(FlexContainer)`
  gap: 6px;
  width: 100%;
  position: relative;
`;

export const FooterContentStyled = styled(Typography)`
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  font-size: 0.625rem;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.primary.variant9};
  flex: 1;
  position: relative;
`;

export const GradeStyled = styled(Typography)<GradeStylesT>`
  margin-right: 10px;
  font-size: 2.3rem;
  font-weight: ${({ theme }) => theme.fontWeight.black};
  ${({ grade }) => gradeColor[grade]}
  position: relative;
`;

export const HeaderStyled = styled(FlexContainer)<ReportStateStyledT>`
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  background-color: ${({ theme }) => theme.colors.white};
  padding: 1rem;
  margin-bottom: ${({ isReportOpened }) => (isReportOpened ? 0 : '10px')};
  z-index: 1;
  transition: margin-bottom 0.3s linear;
  position: relative;
  flex-direction: column-reverse;
  ${({ theme }) => `
    ${theme.devices.tablet} {
      flex-direction: row;
      gap: 3rem;
    }
  `}
`;

export const FlexContainerStyled = styled(FlexContainer)`
  gap: 0.75rem;
  position: relative;
  margin-left: auto;
`;

export const ButtonCollapseStyled = styled(ButtonIcon)<ReportStateStyledT>`
  position: relative;
  height: 26px;

  svg {
    transform: rotate(${({ isReportOpened }) => (isReportOpened ? '180deg' : '0')});
    transition: 0.3s;
  }

  &:hover {
    svg {
      transform: rotate(${({ isReportOpened }) => (isReportOpened ? '0' : '180deg')});
    }
  }
`;

export const HelpBtnIconStyled = styled(ButtonIcon)`
  padding: 0;
`;

export const getPopupContentStyle = (theme: DefaultTheme, isMobile: boolean) => ({
  minWidth: isMobile ? '180px' : '145px',
  borderRadius: theme.radius.sm,
});

export const MenuItemStyled = styled.li`
  &:hover {
    background-color: ${({ theme }) => theme.colors.white};
  }

  &:only-of-type {
    border-radius: ${({ theme }) => `${theme.radius.sm}`};
  }

  &:not(:only-of-type) {
    &:first-of-type {
      border-radius: ${({ theme }) => `${theme.radius.sm} ${theme.radius.sm} 0 0`};
    }

    &:last-of-type {
      border-radius: ${({ theme }) => `0 0 ${theme.radius.sm} ${theme.radius.sm}`};
    }
  }
`;

const baseNavControl = css`
  width: 100%;
  height: auto;
  justify-content: flex-start;
  padding: 1rem 0.875rem;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  font-size: ${({ theme }) => theme.fontSize['2xl']};
  z-index: 1;
  position: relative;
`;

export const MenuBtnStyled = styled(Button)`
  ${baseNavControl}

  ${({ theme }) => `
    ${theme.devices.tablet} {
      font-size: ${theme.fontSize.md};
      padding: 0.875rem;
    }
  `}
`;

export const LinkStyled = styled(Link)`
  ${baseNavControl}

  display: inline-flex;
  color: ${({ theme }) => theme.colors.primary.variant1};

  ${({ theme }) => `
    ${theme.devices.tablet} {
      font-size: ${theme.fontSize.md};
      padding: 0.875rem;
    }
  `}
`;

export const InnerContainerStyled = styled.div`
  width: 100%;
`;

export const ClaimLabelStyled = styled(Typography)`
  margin: 16px 0 4px;
`;

export const StatisticSignStyled = styled.div<StatisticSignStyledT>`
  width: 0;
  height: 0;
  margin: 0 4px 0 6px;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-bottom: ${({ isPositive, theme }) =>
    isPositive ? `6px solid ${theme.colors.secondary.variant6}` : 'none'};
  border-top: ${({ isPositive, theme }) =>
    isPositive ? 'none' : `6px solid ${theme.colors.highlight.variant5}`};
  display: ${({ isPositive }) => (isPositive ? 'block' : 'none')};
`;

export const InvoicePercentageStyled = styled(Typography)<InvoicePercentageStyledT>`
  color: ${({ isPositive, theme }) =>
    isPositive ? theme.colors.secondary.variant1 : theme.colors.highlight.variant2};
  display: ${({ isPositive }) => (isPositive ? 'block' : 'none')};
`;

export const InnerFlexContainerStyled = styled(InnerContainerStyled)`
  display: flex;
  height: 100%;
  flex-direction: column;
  justify-content: flex-end;
`;

export const StatisticSignLargeStyled = styled(StatisticSignStyled)`
  margin: 0 4px;
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-bottom: ${({ isPositive, theme }) =>
    isPositive ? `8px solid ${theme.colors.secondary.variant6}` : 'none'};
  border-top: ${({ isPositive, theme }) =>
    isPositive ? 'none' : `8px solid ${theme.colors.highlight.variant5}`};
  display: ${({ isPositive }) => (isPositive ? 'block' : 'none')};
`;
